import { useEffect, useRef, createRef, useState, useCallback } from 'react';
import * as L from 'leaflet';
import RasterCoords from 'leaflet-rastercoords';
import '../../utils/leafletIconFix.js';
import MapEditor from './MapEditor.jsx';
import MapControls from './MapControls.jsx';
import MapItems from './MapItems.jsx';
import MapDatabaseSidebar from './MapDatabaseSidebar.jsx';
import MapTileLayerGeo from './MapTileLayerGeo.jsx';
import MapTileLayerUrl from './MapTileLayerUrl.jsx';
import MapGPSLocator from './MapGPSLocator.jsx';
import MapControlsToggleBtn from './MapControlsToggleBtn.jsx';
import { Square3Stack3DIcon, MapIcon, ServerStackIcon, ViewfinderCircleIcon, MagnifyingGlassCircleIcon } from '@heroicons/react/24/outline'
import { 
    ServerStackIcon as ServerStackIcon_solid,
    Square3Stack3DIcon as Square3Stack3DIcon_solid,
    MapIcon as MapIcon_solid,
    ViewfinderCircleIcon as ViewfinderCircleIcon_solid,
    MagnifyingGlassCircleIcon as MagnifyingGlassCircleIcon_solid
 } from '@heroicons/react/24/solid'
import useAccessLevel from '/src/hooks/useAccessLevel.js';
import MapSearch from './MapSearch.jsx';

const osm = {
    url: 'http://{s}.tile.osm.org/{z}/{x}/{y}.png',
    attribution: '&copy; <a href="http://osm.org/copyright">OpenStreetMap</a> contributors',
}

export default function MapBase({}){

    const [map, setMap] = useState(null);
    const [metadata, setMetadata] = useState(null);
    const [mapEl, setMapEl] = useState(null);
    const getMapEl = useCallback(node=>setMapEl(node), []);
    const [showOverlay, setShowOverlay] = useState(false);
    const [showOsm, setShowOsm] = useState(false);
    const [showLocate, setShowLocate] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [showSearch, setShowSearch] = useState(false);
    const [selectedPlot, setSelectedPlot] = useState(null);
    const [filterPlots, setFilterPlots] = useState(null);

    const canImportPlots = useAccessLevel('admin');

    // init map
    useEffect(()=>{
        console.debug('base got mapEl', mapEl, 'and map', map);
        if(mapEl && (!map || map._container !== mapEl)){
            console.debug('base creating new map')
            const newMap = L.map(mapEl, {
                // crs: L.CRS.Simple,
                // center: [44.991846227, -73.4478950996],
                // zoom: 17,
                minZoom: 17,
                // maxZoom: 21,
                attributionControl: false,
            });

            // Attribution
            // L.control.attribution({
            //     prefix: '© Sensing Places LLC',
            // }).addTo(newMap);

            // Debug
            // newMap.on('zoomend', ()=>{
            //     console.debug(newMap.getZoom());
            // })

            setMap(newMap);  
        }
        return ()=>{
            // Clean up base map
            console.debug('base cleanup', map);
            if(map){ 
                map.eachLayer((layer)=> {
                    map.removeLayer(layer);
                });
                map.remove();
            }
        }
    },[mapEl]);

    // Finish setup after metadata comes back from base layer
    useEffect(()=>{
        if(metadata && map){
            // Set limits
            map.setMinZoom(metadata.minZoom);
            map.setMaxBounds(metadata.bounds);
            // Set View
            map.setView(metadata.center, parseInt((metadata.maxZoom + metadata.minZoom) / 2));
        }
    },[metadata, map]);
    
    function handleControlsClick(e, name){
        if(name == 'search-toggle'){
            const state = !showSearch;
            setShowSearch(state);
            setFilterPlots(null);
        }else if(name == 'overlay-toggle'){
            const state = !showOverlay;
            setShowOverlay(state);
        }else if(name == 'osm-toggle'){
            const state = !showOsm;
            setShowOsm(state);
            if(state && map){
                map.setZoom(18);
            }
        }else if(name == 'locate-toggle'){
            const state = !showLocate;
            setShowLocate(state);
        }else if(name == 'csv-import'){
            setShowImport(s=>!s);
            setSelectedPlot(null);
        }
    }

    function onSelectPlot(_id){
        // console.debug('onSelectPlot', _id);
        setSelectedPlot(_id || null);
        if(_id) setShowImport(false);
        setShowSearch(false);
        setFilterPlots(null);
    }

    // Changes the displayed plots to match the search results.
    // useCallback prevents infinite render loop.
    const onSearchResults = useCallback(data=>setFilterPlots(data || null), []);

    return (
        <div className='flex flex-grow bg-gray-100 relative'>
            <div className='flex flex-grow bg-gray-100 relative'>
                <div ref={getMapEl} className='flex-grow z-0'></div>
                <MapTileLayerGeo map={map} mapName='base' attribution={'© Grunts Pilots Bots'} show={true} setView={true} onMetadata={data=>setMetadata(data)} />
                <MapTileLayerGeo map={map} mapName='overlay' show={showOverlay} setView={true} zIndex={1} />
                <MapTileLayerUrl map={map} mapUrl={osm.url} attribution={osm.attribution} show={showOsm} zIndex={2} />
                {/* <MapTileLayer map={map} mapName='base' attribution={'© Grunts Pilots Bots'} show={true} onRc={rc=>setRc(rc)} /> */}
                {/* <MapTileLayer map={map} mapName='overlay' show={showOverlay} /> */}
                {/* {mapBaseUri && <MapImageLayer map={map} rc={rc} show={showOverlay} imgUrl={`${mapBaseUri}/overlay.svg`}  />} */}
                <MapGPSLocator map={map} active={showLocate} maxBounds={metadata && metadata.bounds} />
                <MapItems map={map} onSelectItem={data=>onSelectPlot(data && data._id)} selectedItem={selectedPlot} filterItems={filterPlots} />
                <MapControls>
                    <MapControlsToggleBtn state={showSearch} onClick={e=>handleControlsClick(e, 'search-toggle')} title="Toggle Search">
                        <MagnifyingGlassCircleIcon />
                        <MagnifyingGlassCircleIcon_solid />
                    </MapControlsToggleBtn>
                    <MapControlsToggleBtn state={showOverlay} onClick={e=>handleControlsClick(e, 'overlay-toggle')} title="Toggle Overlay">
                        <Square3Stack3DIcon />
                        <Square3Stack3DIcon_solid />
                    </MapControlsToggleBtn>
                    <MapControlsToggleBtn state={showOsm} onClick={e=>handleControlsClick(e, 'osm-toggle')} title="Toggle Open Street Map">
                        <MapIcon />
                        <MapIcon_solid />
                    </MapControlsToggleBtn>
                    <MapControlsToggleBtn state={showLocate} onClick={e=>handleControlsClick(e, 'locate-toggle')} title="Find Me">
                        <ViewfinderCircleIcon />
                        <ViewfinderCircleIcon_solid />
                    </MapControlsToggleBtn>
                    {canImportPlots && <MapControlsToggleBtn state={showImport} onClick={e=>handleControlsClick(e, 'csv-import')} title="Import CSV">
                        <ServerStackIcon />
                        <ServerStackIcon_solid />
                    </MapControlsToggleBtn>}
                </MapControls>
            </div>
            <MapEditor map={map} _id={selectedPlot} onNew={()=>onSelectPlot(null)} onNewSave={(_id)=>onSelectPlot(_id)} onClose={()=>onSelectPlot(null)} />
            {canImportPlots && <MapDatabaseSidebar open={showImport} onClose={()=>setShowImport(false)} />}
            {showSearch && <MapSearch onSelectPlot={(_id)=>onSelectPlot(_id)} onResults={onSearchResults} />}
        </div>
    )
}